/**
 * Cash Register — daily manager entries via Zoho form webhook.
 * Shared cash box: current balance = latest reported balance.
 * Quick computed Q&A (no AI), reconciliation ✓/⚠ per entry.
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import api from '../lib/api';
import { toast } from 'sonner';
import { RefreshCw, Loader2, Wallet, TrendingDown, PiggyBank, AlertTriangle, Copy, MessageCircle } from 'lucide-react';

const fmtInr = (n) => (n == null || isNaN(n)) ? '—' : `₹${Math.round(n).toLocaleString('en-IN')}`;
const fmtDT = (iso) => iso ? new Date(iso).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';

function rangeFor(preset) {
  const t = new Date(); const y = t.getFullYear(), m = t.getMonth();
  const iso = (d) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
  if (preset === 'this_month') return { from: iso(new Date(y, m, 1)), to: iso(new Date(y, m + 1, 0)) };
  if (preset === 'last_month') return { from: iso(new Date(y, m - 1, 1)), to: iso(new Date(y, m, 0)) };
  return { from: '', to: '' };
}

export default function CashRegisterPage() {
  const [preset, setPreset] = useState('this_month');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [manager, setManager] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [answer, setAnswer] = useState(null);

  const range = useMemo(() => preset === 'custom' ? { from: customFrom, to: customTo } : rangeFor(preset), [preset, customFrom, customTo]);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (range.from) params.from_date = range.from;
      if (range.to) params.to_date = range.to;
      const r = await api.get('/cash-register/report', { params });
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load cash register');
    } finally { setLoading(false); }
  }, [range.from, range.to]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const copyUrl = () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}${data.form.webhook_url}`;
    navigator.clipboard.writeText(url);
    toast.success('Webhook URL copied');
  };

  const entries = useMemo(() => {
    if (!data) return [];
    return manager ? data.entries.filter((e) => e.manager === manager) : data.entries;
  }, [data, manager]);

  const managerNames = useMemo(() => (data?.managers || []).map((m) => m.manager), [data]);

  const questions = useMemo(() => {
    if (!data) return [];
    const s = data.summary;
    const weekAgo = new Date(Date.now() - 6 * 864e5).toISOString().slice(0, 10);
    return [
      {
        q: 'What is the current cash balance?',
        a: () => data.latest_entry
          ? `${fmtInr(data.current_balance)} — latest closing balance reported by ${data.latest_entry.manager} on ${fmtDT(data.latest_entry.received_at)} (entry date ${data.latest_entry.entry_date}). Shared cash box: the newest entry wins.`
          : 'No entries with a reported balance yet.',
      },
      {
        q: 'How much did we spend this week?',
        a: () => {
          const wk = data.entries.filter((e) => e.entry_date >= weekAgo);
          const tot = wk.reduce((a, e) => a + e.total_spent, 0);
          return `${fmtInr(tot)} across ${wk.reduce((a, e) => a + e.expenses.length, 0)} expenses in the last 7 days (${wk.length} entries).`;
        },
      },
      {
        q: 'Biggest expense in this range?',
        a: () => {
          let best = null, who = '', when = '';
          for (const e of data.entries) for (const x of e.expenses) if (!best || x.amount > best.amount) { best = x; who = e.manager; when = e.entry_date; }
          return best ? `${fmtInr(best.amount)} — "${best.details}" by ${who} on ${when}.` : 'No expenses recorded in this range.';
        },
      },
      {
        q: 'Total credit deposits in this range?',
        a: () => {
          const per = {};
          for (const e of data.entries) if (e.deposit_amount) per[e.manager] = (per[e.manager] || 0) + e.deposit_amount;
          const parts = Object.entries(per).map(([m, v]) => `${m} ${fmtInr(v)}`).join(', ');
          return `${fmtInr(s.deposits_range)} across ${s.deposit_count_range} deposits${parts ? ` — ${parts}` : ''}.`;
        },
      },
      {
        q: 'Which managers missed an entry today?',
        a: () => {
          const missed = (data.managers || []).filter((m) => m.entry_date < data.today).map((m) => `${m.manager} (last: ${m.entry_date})`);
          return missed.length ? `⚠ ${missed.join(' · ')}` : 'All managers have filed an entry today ✓';
        },
      },
      {
        q: 'Any reconciliation mismatches?',
        a: () => {
          const bad = data.entries.filter((e) => e.delta != null && Math.abs(e.delta) > 0.5);
          return bad.length
            ? `⚠ ${bad.length} mismatched ${bad.length === 1 ? 'entry' : 'entries'}: ${bad.slice(0, 3).map((e) => `${e.manager} ${e.entry_date} (off by ${fmtInr(e.delta)})`).join(' · ')}${bad.length > 3 ? '…' : ''}`
            : 'All entries reconcile ✓ (reported balance matches previous balance − spend + deposit)';
        },
      },
    ];
  }, [data]);

  const s = data?.summary;

  return (
    <div className="space-y-5" data-testid="cash-register-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Cash Register</h1>
          <p className="text-sm text-slate-500 mt-0.5">Daily manager cash entries via Zoho form webhook · up to 3 expenses/day + credit deposits · shared cash box</p>
        </div>
        <Button variant="ghost" size="sm" onClick={fetchReport} data-testid="cash-reload-btn"><RefreshCw className="w-3.5 h-3.5 mr-1.5" />Reload</Button>
      </div>

      {data && !data.form && (
        <Card className="p-6 text-center space-y-2" data-testid="cash-setup-card">
          <p className="text-sm text-slate-600">No form with type <b>Cash Register</b> is registered yet.</p>
          <p className="text-sm text-slate-500">Go to <Link to="/webhooks" className="text-violet-600 font-medium hover:underline" data-testid="cash-goto-webhooks-link">Webhook Configuration</Link>, register your Zoho form and pick form type <b>"Cash Register"</b> — its webhook URL will appear here automatically.</p>
        </Card>
      )}

      {data?.form && (
        <div className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 flex flex-wrap items-center gap-2" data-testid="cash-webhook-banner">
          <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Webhook URL</span>
          <code className="text-xs text-violet-700 truncate max-w-xl">{process.env.REACT_APP_BACKEND_URL}{data.form.webhook_url}</code>
          <button type="button" onClick={copyUrl} className="text-[11px] flex items-center gap-1 border border-slate-200 bg-white rounded-md px-2 py-0.5 hover:bg-slate-50" data-testid="cash-copy-url-btn"><Copy className="w-3 h-3" />Copy</button>
          <span className="text-[11px] text-slate-400 ml-auto">{data.form.name} · {data.form.submission_count} submissions · managed in <Link to="/webhooks" className="text-violet-600 hover:underline">Webhook Configuration</Link></span>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2" data-testid="cash-filter-bar">
        <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Range</span>
        {[['this_month', 'This month'], ['last_month', 'Last month'], ['all', 'All time'], ['custom', 'Custom']].map(([id, label]) => (
          <button key={id} type="button" onClick={() => setPreset(id)} data-testid={`cash-preset-${id}`}
            className={`text-[11px] px-2.5 py-1 rounded-md border transition ${preset === id ? 'bg-violet-600 text-white border-violet-600' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`}>{label}</button>
        ))}
        {preset === 'custom' && (
          <span className="flex items-center gap-1">
            <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} className="text-[11px] border border-slate-200 rounded-md px-2 py-1" data-testid="cash-custom-from" />
            <span className="text-slate-400 text-xs">→</span>
            <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} className="text-[11px] border border-slate-200 rounded-md px-2 py-1" data-testid="cash-custom-to" />
          </span>
        )}
        <div className="ml-auto flex items-center gap-1.5">
          <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Manager</span>
          <select value={manager} onChange={(e) => setManager(e.target.value)} className="text-xs border border-slate-200 rounded-md px-2 py-1.5 bg-white min-w-[150px]" data-testid="cash-manager-select">
            <option value="">All managers</option>
            {managerNames.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-slate-400"><Loader2 className="w-6 h-6 animate-spin mr-2" />Loading…</div>
      ) : data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="cash-kpis">
            {[
              { label: 'Current Cash Balance', value: fmtInr(data.current_balance), sub: data.latest_entry ? `by ${data.latest_entry.manager} · ${fmtDT(data.latest_entry.received_at)}` : 'no entries yet', icon: Wallet, tint: 'text-violet-600 bg-violet-50' },
              { label: 'Spent Today', value: fmtInr(s.spent_today), sub: `${s.expenses_today} expenses`, icon: TrendingDown, tint: 'text-rose-600 bg-rose-50' },
              { label: 'Spent (range)', value: fmtInr(s.spent_range), sub: `${s.expense_count_range} expenses`, icon: TrendingDown, tint: 'text-rose-600 bg-rose-50' },
              { label: 'Credit Deposits (range)', value: fmtInr(s.deposits_range), sub: `${s.deposit_count_range} deposits`, icon: PiggyBank, tint: 'text-emerald-600 bg-emerald-50' },
              { label: 'Reconciliation', value: s.mismatches_range ? `${s.mismatches_range} ⚠` : '✓ OK', sub: s.mismatches_range ? 'reported ≠ computed balance' : 'all entries reconcile', icon: AlertTriangle, tint: s.mismatches_range ? 'text-amber-600 bg-amber-50' : 'text-emerald-600 bg-emerald-50' },
            ].map((k) => (
              <Card key={k.label} className="p-4" data-testid={`cash-kpi-${k.label.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center mb-2 ${k.tint}`}><k.icon className="w-4 h-4" /></div>
                <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{k.label}</div>
                <div className="text-xl font-bold text-slate-900 mt-0.5">{k.value}</div>
                <div className="text-[11px] text-slate-400 mt-0.5 truncate">{k.sub}</div>
              </Card>
            ))}
          </div>

          <Card className="p-4 bg-gradient-to-r from-violet-50/60 to-white border-violet-200" data-testid="cash-ask-panel">
            <div className="text-xs font-semibold text-violet-700 mb-2 flex items-center gap-1.5"><MessageCircle className="w-3.5 h-3.5" />Ask the register</div>
            <div className="flex flex-wrap gap-1.5">
              {questions.map((q) => (
                <button key={q.q} type="button" onClick={() => setAnswer({ q: q.q, text: q.a() })} data-testid={`cash-question-chip`}
                  className={`text-[11px] px-2.5 py-1 rounded-full border transition ${answer?.q === q.q ? 'bg-violet-600 text-white border-violet-600' : 'bg-white border-slate-200 text-slate-600 hover:bg-violet-50'}`}>{q.q}</button>
              ))}
            </div>
            {answer && (
              <div className="mt-3 bg-white border border-slate-100 rounded-lg p-3 text-sm text-slate-700" data-testid="cash-answer-card">{answer.text}</div>
            )}
          </Card>

          <Card className="overflow-hidden" data-testid="cash-entries-table">
            <div className="px-4 pt-4 pb-2">
              <h2 className="text-sm font-semibold text-slate-800">Daily Entries ({entries.length})</h2>
              <p className="text-[11px] text-slate-400">One row per form submission · ⚠ = reported balance doesn't match previous balance − spend + deposit</p>
            </div>
            {entries.length === 0 ? (
              <div className="px-4 pb-6 text-sm text-slate-400" data-testid="cash-empty">No entries in this range yet. Submit your Zoho form (or a test POST to the webhook URL) and hit Reload.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-100">
                    <th className="text-left px-4 py-2">Date</th><th className="text-left px-3 py-2">Manager</th>
                    <th className="text-left px-3 py-2">Branch</th>
                    <th className="text-left px-3 py-2">Expenses (max 3)</th><th className="text-right px-3 py-2">Total Spent</th>
                    <th className="text-right px-3 py-2">Credit Deposit</th><th className="text-left px-3 py-2">Deposit Details</th>
                    <th className="text-right px-3 py-2">Reported Balance</th><th className="text-center px-4 py-2">Check</th>
                  </tr></thead>
                  <tbody>
                    {entries.map((e) => {
                      const bad = e.delta != null && Math.abs(e.delta) > 0.5;
                      return (
                        <tr key={e.submission_id} className={`border-b border-slate-50 ${bad ? 'bg-amber-50/50' : ''}`} data-testid={`cash-entry-row-${e.submission_id}`}>
                          <td className="px-4 py-2.5 whitespace-nowrap" title={e.expense_id || ''}>{e.entry_date}{e.is_test && <Badge variant="secondary" className="ml-1 text-[9px] px-1 py-0 bg-sky-100 text-sky-600">test</Badge>}</td>
                          <td className="px-3 py-2.5 font-medium text-slate-800" title={e.paid_by ? `Paid by: ${e.paid_by}` : ''}>{e.manager}</td>
                          <td className="px-3 py-2.5 text-xs text-slate-500">{e.branch || '—'}</td>
                          <td className="px-3 py-2.5 text-xs text-slate-500">{e.expenses.length ? e.expenses.map((x) => `${x.details} ${fmtInr(x.amount)}`).join(' · ') : '—'}</td>
                          <td className="px-3 py-2.5 text-right font-semibold text-rose-600">{e.total_spent ? fmtInr(e.total_spent) : '—'}</td>
                          <td className="px-3 py-2.5 text-right font-semibold text-emerald-600">{e.deposit_amount ? fmtInr(e.deposit_amount) : '—'}</td>
                          <td className="px-3 py-2.5 text-xs text-slate-500 max-w-[220px] truncate">{e.deposit_details || '—'}</td>
                          <td className="px-3 py-2.5 text-right font-bold text-slate-900" title={e.opening_balance != null ? `Opening: ${fmtInr(e.opening_balance)}` : ''}>{fmtInr(e.reported_balance)}</td>
                          <td className="px-4 py-2.5 text-center" title={e.computed_balance != null ? `Computed: ${fmtInr(e.computed_balance)}` : ''}>
                            {e.delta == null ? <span className="text-slate-300">—</span> : bad ? <span className="text-amber-600 font-semibold text-xs">⚠ {e.delta > 0 ? '+' : ''}{fmtInr(e.delta)}</span> : <span className="text-emerald-600">✓</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          <Card className="p-4" data-testid="cash-managers-panel">
            <h2 className="text-sm font-semibold text-slate-800 mb-2">Manager Activity (latest entry)</h2>
            <table className="w-full text-sm">
              <tbody>
                {(data.managers || []).map((m) => (
                  <tr key={m.manager} className="border-b border-slate-50 last:border-0">
                    <td className="py-2 font-medium text-slate-800">{m.manager}{m.entry_date < data.today && <span className="text-amber-600 text-[11px] ml-2">⚠ no entry today</span>}</td>
                    <td className="py-2 text-right font-bold">{fmtInr(m.reported_balance)}</td>
                    <td className="py-2 text-right text-[11px] text-slate-400 pl-3">{fmtDT(m.received_at)}</td>
                  </tr>
                ))}
                {(data.managers || []).length === 0 && <tr><td className="py-2 text-sm text-slate-400">No managers yet</td></tr>}
              </tbody>
            </table>
          </Card>
        </>
      )}
    </div>
  );
}
